import React, { useState, useEffect, useCallback } from 'react';
import { MapPin, Truck, Package, Calendar, X, RefreshCw } from 'lucide-react';
import { DeliveryRequestApi } from '@shared/sdk-v1/apis';
import { DeliveryRequest as DeliveryRequestModel } from '@shared/sdk-v1/models';
import { apiConfig } from '../shared/api-config';
import { useAuth } from '../auth/hooks/useAuth';
import { showErrorToast, showSuccessToast } from '../utils/toast';
import { Button, InputField } from '@shared/components';
import { useSelector } from 'react-redux';
import { getCourierId } from '@state/courierSlice';

type Tab = 'new' | 'history';
type Status = 'pending' | 'confirmed' | 'in_transit' | 'delivered' | 'cancelled';

const STATUS_CONFIG: Record<Status, { label: string; bg: string; text: string }> = {
  pending: { label: 'Pending', bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-700 dark:text-yellow-400' },
  confirmed: { label: 'Confirmed', bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-400' },
  in_transit: { label: 'In Transit', bg: 'bg-purple-100 dark:bg-purple-900/30', text: 'text-purple-700 dark:text-purple-400' },
  delivered: { label: 'Delivered', bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-400' },
  cancelled: { label: 'Cancelled', bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-400' },
};

const VEHICLE_OPTIONS = [
  { value: 'motorbike', label: 'Motorbike' },
  { value: 'car', label: 'Car' },
  { value: 'van', label: 'Van' },
];

const TIME_OPTIONS = [
  { value: 'morning', label: 'Morning (8AM - 12PM)' },
  { value: 'afternoon', label: 'Afternoon (12PM - 4PM)' },
  { value: 'evening', label: 'Evening (4PM - 8PM)' },
  { value: 'anytime', label: 'Anytime' },
];

const LocalDelivery = () => {
  const { user } = useAuth();
  const courierId = useSelector(getCourierId);
  const [activeTab, setActiveTab] = useState<Tab>('new');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deliveries, setDeliveries] = useState<DeliveryRequestModel[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [form, setForm] = useState({
    pickupAddress: '',
    pickupContactName: '',
    pickupContactPhone: '',
    dropoffAddress: '',
    dropoffContactName: '',
    dropoffContactPhone: '',
    itemDescription: '',
    vehicleType: 'motorbike',
    date: '',
    preferredTime: 'anytime',
    notes: '',
  });

  const updateField = (field: string, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const fetchDeliveries = useCallback(async () => {
    if (!user?.id || !courierId) return;
    setIsLoading(true);
    try {
      const api = new DeliveryRequestApi(apiConfig());
      const results = await api.deliveryRequestFind({
        filter: JSON.stringify({
          where: { customerId: user.id, courierId, deliveryMode: 'local' },
          order: 'createdAt DESC',
          limit: 50,
        }),
      });
      setDeliveries(results);
    } catch (err) {
      console.error('Failed to fetch deliveries', err);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id, courierId]);

  useEffect(() => {
    if (activeTab === 'history') fetchDeliveries();
  }, [activeTab, fetchDeliveries]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id || !courierId) {
      showErrorToast({ title: 'Error', message: 'Authentication required' });
      return;
    }

    const required = ['pickupAddress', 'pickupContactName', 'pickupContactPhone', 'dropoffAddress', 'dropoffContactName', 'dropoffContactPhone', 'itemDescription', 'date'] as const;
    for (const field of required) {
      if (!form[field].trim()) {
        showErrorToast({ title: 'Missing Field', message: `Please fill in all required fields` });
        return;
      }
    }

    setIsSubmitting(true);
    try {
      const api = new DeliveryRequestApi(apiConfig());
      await api.deliveryRequestReplaceOrCreatePostDeliveryRequestsReplaceOrCreate({
        data: {
          deliveryMode: 'local',
          pickupAddress: form.pickupAddress,
          pickupContactName: form.pickupContactName,
          pickupContactPhone: form.pickupContactPhone,
          dropoffAddress: form.dropoffAddress,
          dropoffContactName: form.dropoffContactName,
          dropoffContactPhone: form.dropoffContactPhone,
          itemDescription: form.itemDescription,
          vehicleType: form.vehicleType,
          date: new Date(form.date),
          preferredTime: form.preferredTime,
          notes: form.notes || undefined,
          status: 'pending',
          courierId: Number(courierId),
          customerId: Number(user.id),
        } as DeliveryRequestModel,
      });

      showSuccessToast({ title: 'Success', message: 'Delivery request submitted!' });
      setForm({
        pickupAddress: '', pickupContactName: '', pickupContactPhone: '',
        dropoffAddress: '', dropoffContactName: '', dropoffContactPhone: '',
        itemDescription: '', vehicleType: 'motorbike', date: '', preferredTime: 'anytime', notes: '',
      });
      setActiveTab('history');
    } catch (err: any) {
      console.error('Failed to create delivery request', err);
      showErrorToast({ title: 'Error', message: err?.message || 'Failed to submit request' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = async (id: number) => {
    try {
      const api = new DeliveryRequestApi(apiConfig());
      await api.deliveryRequestPrototypePatchAttributes({
        id: String(id),
        data: { status: 'cancelled' } as any,
      });
      showSuccessToast({ title: 'Cancelled', message: 'Delivery request cancelled' });
      fetchDeliveries();
    } catch (err) {
      showErrorToast({ title: 'Error', message: 'Failed to cancel request' });
    }
  };

  const statusBadge = (status: string) => {
    const config = STATUS_CONFIG[(status as Status)] || STATUS_CONFIG.pending;
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 pb-24">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Send a Package</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Request a local delivery from any pickup to any dropoff location
        </p>
      </div>

      {/* Tab Toggle */}
      <div className="flex bg-gray-100 dark:bg-gray-800 rounded-lg p-1 mb-6">
        {(['new', 'history'] as Tab[]).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${
              activeTab === tab
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            {tab === 'new' ? 'New Request' : 'My Deliveries'}
          </button>
        ))}
      </div>

      {/* New Request Form */}
      {activeTab === 'new' && (
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Pickup Section */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="p-1.5 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <MapPin className="h-4 w-4 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="font-semibold text-gray-900 dark:text-white">Pickup Details</h3>
            </div>
            <div className="space-y-3">
              <InputField label="Pickup Address" value={form.pickupAddress} onChange={(e: any) => updateField('pickupAddress', e.target.value)} required placeholder="Enter pickup address" />
              <InputField label="Contact Name" value={form.pickupContactName} onChange={(e: any) => updateField('pickupContactName', e.target.value)} required placeholder="Who to contact at pickup" />
              <InputField label="Contact Phone" value={form.pickupContactPhone} onChange={(e: any) => updateField('pickupContactPhone', e.target.value)} required placeholder="Phone number" />
            </div>
          </div>

          {/* Dropoff Section */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="p-1.5 bg-red-100 dark:bg-red-900/30 rounded-lg">
                <MapPin className="h-4 w-4 text-red-600 dark:text-red-400" />
              </div>
              <h3 className="font-semibold text-gray-900 dark:text-white">Dropoff Details</h3>
            </div>
            <div className="space-y-3">
              <InputField label="Dropoff Address" value={form.dropoffAddress} onChange={(e: any) => updateField('dropoffAddress', e.target.value)} required placeholder="Enter dropoff address" />
              <InputField label="Contact Name" value={form.dropoffContactName} onChange={(e: any) => updateField('dropoffContactName', e.target.value)} required placeholder="Who to contact at dropoff" />
              <InputField label="Contact Phone" value={form.dropoffContactPhone} onChange={(e: any) => updateField('dropoffContactPhone', e.target.value)} required placeholder="Phone number" />
            </div>
          </div>

          {/* Package Details */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="p-1.5 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <Package className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="font-semibold text-gray-900 dark:text-white">Package Details</h3>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">What are you sending? *</label>
                <textarea
                  value={form.itemDescription}
                  onChange={e => updateField('itemDescription', e.target.value)}
                  required
                  rows={2}
                  placeholder="Describe the item(s)"
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Vehicle Type</label>
                <select
                  value={form.vehicleType}
                  onChange={e => updateField('vehicleType', e.target.value)}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  {VEHICLE_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Schedule */}
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="p-1.5 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                <Calendar className="h-4 w-4 text-orange-600 dark:text-orange-400" />
              </div>
              <h3 className="font-semibold text-gray-900 dark:text-white">Schedule</h3>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Preferred Date *</label>
                <input
                  type="date"
                  value={form.date}
                  onChange={e => updateField('date', e.target.value)}
                  required
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Preferred Time</label>
                <select
                  value={form.preferredTime}
                  onChange={e => updateField('preferredTime', e.target.value)}
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  {TIME_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Notes (optional)</label>
                <textarea
                  value={form.notes}
                  onChange={e => updateField('notes', e.target.value)}
                  rows={2}
                  placeholder="Any special instructions"
                  className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white px-3 py-2 text-sm focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>
          </div>

          <Button type="submit" disabled={isSubmitting} className="w-full bg-primary hover:opacity-90 text-white font-medium py-3 rounded-lg">
            {isSubmitting ? 'Submitting...' : 'Request Delivery'}
          </Button>
        </form>
      )}

      {/* My Deliveries */}
      {activeTab === 'history' && (
        <div>
          <div className="flex justify-end mb-3">
            <button onClick={fetchDeliveries} className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1 hover:text-gray-700 dark:hover:text-gray-200">
              <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} /> Refresh
            </button>
          </div>

          {isLoading && deliveries.length === 0 ? (
            <div className="text-center py-12 text-gray-400">Loading...</div>
          ) : deliveries.length === 0 ? (
            <div className="text-center py-12">
              <Package className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              <p className="text-gray-500 dark:text-gray-400 font-medium">No deliveries yet</p>
              <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Your local delivery requests will appear here</p>
            </div>
          ) : (
            <div className="space-y-3">
              {deliveries.map(d => (
                <div key={d.id} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 text-sm">
                        <MapPin className="h-3.5 w-3.5 text-green-500 flex-shrink-0" />
                        <span className="text-gray-700 dark:text-gray-300 truncate">{d.pickupAddress}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm mt-1">
                        <MapPin className="h-3.5 w-3.5 text-red-500 flex-shrink-0" />
                        <span className="text-gray-700 dark:text-gray-300 truncate">{d.dropoffAddress}</span>
                      </div>
                    </div>
                    {statusBadge(d.status || 'pending')}
                  </div>

                  <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400 mt-2">
                    {d.itemDescription && <span className="truncate max-w-[140px]">{d.itemDescription}</span>}
                    {d.vehicleType && <span className="capitalize">{d.vehicleType}</span>}
                    {d.date && !isNaN(new Date(d.date).getTime()) && <span>{new Date(d.date).toLocaleDateString()}</span>}
                  </div>

                  {d.status === 'pending' && d.id && (
                    <button
                      onClick={() => handleCancel(d.id!)}
                      className="mt-3 flex items-center gap-1 text-xs text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                    >
                      <X className="h-3.5 w-3.5" /> Cancel Request
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LocalDelivery;
